/**
 * Reusable Image Upload Controller
 *
 * A generic, reusable controller for image uploads with preview, direct upload progress, and removal support.
 * Can be used for resources, case results, blog posts, and other features requiring image uploads.
 *
 * Usage in view:
 *
 * <div data-controller="image-upload"
 *      data-image-upload-max-size-value="5242880"
 *      data-image-upload-accepted-types-value='["image/png","image/jpeg","image/webp"]'>
 *
 *   <!-- Current image (for edit forms) -->
 *   <div data-image-upload-target="currentImage">
 *     <img src="..." data-image-upload-target="currentImagePreview" />
 *   </div>
 *
 *   <!-- New image preview -->
 *   <div data-image-upload-target="newImagePreview" class="hidden">
 *     <img data-image-upload-target="previewImage" />
 *   </div>
 *
 *   <!-- Upload progress -->
 *   <div data-image-upload-target="progressContainer" class="hidden">
 *     <div data-image-upload-target="progressBar"></div>
 *     <span data-image-upload-target="progressText"></span>
 *   </div>
 *
 *   <!-- File input -->
 *   <input type="file"
 *          data-image-upload-target="input"
 *          data-action="change->image-upload#selectFile
 *                       direct-upload:initialize->image-upload#uploadInitialize
 *                       direct-upload:start->image-upload#uploadStart
 *                       direct-upload:progress->image-upload#uploadProgress
 *                       direct-upload:error->image-upload#uploadError
 *                       direct-upload:end->image-upload#uploadEnd"
 *          direct_upload="true" />
 *
 *   <!-- Remove checkbox (for edit) -->
 *   <input type="checkbox"
 *          data-image-upload-target="removeCheckbox"
 *          data-action="change->image-upload#toggleRemove" />
 *
 *   <!-- Error display -->
 *   <div data-image-upload-target="error" class="hidden"></div>
 * </div>
 */

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",                  // File input element
    "currentImage",           // Container for existing image
    "currentImagePreview",    // The existing image element
    "newImagePreview",        // Container for new image preview
    "previewImage",           // The preview image element
    "progressContainer",      // Upload progress container
    "progressBar",            // Progress bar element
    "progressText",           // Progress percentage text
    "removeCheckbox",         // Remove image checkbox
    "error",                  // Error message container
    "selectButton"            // Optional custom select button
  ]

  static values = {
    maxSize: { type: Number, default: 5242880 },           // 5MB default
    acceptedTypes: { type: Array, default: [ "image/png", "image/jpeg", "image/jpg", "image/webp" ] }
  }

  connect() {
    this.previewUrl = null
    this.hasExistingImage = this.hasCurrentImageTarget &&
                            this.currentImageTarget.querySelector("img")?.src
  }

  disconnect() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = null
    }
  }

  // Trigger file picker (for custom buttons)
  openFilePicker(event) {
    event?.preventDefault()
    if (this.hasInputTarget) this.inputTarget.click()
  }

  // Handle file selection
  selectFile(event) {
    const file = event.target.files[0]
    if (!file) return

    // Clear previous errors
    this.hideError()

    // Validate file
    if (!this.validateFile(file)) return

    // Show preview
    this.showPreview(file)

    // Uncheck remove if checked
    if (this.hasRemoveCheckboxTarget && this.removeCheckboxTarget.checked) {
      this.removeCheckboxTarget.checked = false
    }
  }

  // Validate file size and type
  validateFile(file) {
    if (file.size > this.maxSizeValue) {
      const maxMB = (this.maxSizeValue / 1024 / 1024).toFixed(0)
      this.showError(`File is too large. Maximum size is ${maxMB}MB.`)
      this.inputTarget.value = ""
      return false
    }

    if (!this.acceptedTypesValue.includes(file.type)) {
      this.showError("Invalid file type. Please upload a PNG, JPEG, or WebP image.")
      this.inputTarget.value = ""
      return false
    }

    return true
  }

  // Show image preview
  showPreview(file) {
    // Revoke previous preview URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }

    this.previewUrl = URL.createObjectURL(file)

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = this.previewUrl
    }
    if (this.hasNewImagePreviewTarget) {
      this.newImagePreviewTarget.classList.remove("hidden")
    }
    if (this.hasCurrentImageTarget) {
      this.currentImageTarget.classList.add("hidden")
    }
  }

  // Handle remove checkbox toggle
  toggleRemove(event) {
    if (event.target.checked) {
      // Hide previews, show placeholder or original state
      if (this.hasNewImagePreviewTarget) {
        this.newImagePreviewTarget.classList.add("hidden")
      }
      if (this.hasCurrentImageTarget) {
        this.currentImageTarget.classList.add("hidden")
      }
      // Clear file input
      if (this.hasInputTarget) {
        this.inputTarget.value = ""
      }
      // Clear preview URL
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = null
      }
    } else {
      // Show current image if exists
      if (this.hasExistingImage && this.hasCurrentImageTarget) {
        this.currentImageTarget.classList.remove("hidden")
      }
    }
  }

  // Direct upload handlers
  uploadInitialize() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove("hidden")
    }
    this.updateProgress(0)
  }

  uploadStart() {
    this.updateProgress(0)
  }

  uploadProgress(event) {
    const progress = Math.round(event.detail.progress || 0)
    this.updateProgress(progress)
  }

  uploadError() {
    this.showError("Upload failed. Please try again.")
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden")
    }
  }

  uploadEnd() {
    this.updateProgress(100)
    setTimeout(() => {
      if (this.hasProgressContainerTarget) {
        this.progressContainerTarget.classList.add("hidden")
      }
    }, 500)
  }

  // Update progress display
  updateProgress(percent) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`
    }
  }

  // Error display helpers
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }
}
